import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext';
import { supabase } from '../lib/supabase';
import PaymentMethods from '../components/PaymentMethods';
import { Loader, MapPin } from 'lucide-react';
import AddressForm from '../components/AddressForm';

const Checkout = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useCart();
  const { currentCurrency } = useCurrency();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shippingAddress, setShippingAddress] = useState<any>(null);
  const [selectedPayment, setSelectedPayment] = useState('');
  const [showAddressForm, setShowAddressForm] = useState(false);

  useEffect(() => {
    checkUser();
  }, []);

  const checkUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/auth');
        return;
      }

      // Get user's shipping address
      const { data: profile } = await supabase
        .from('profiles')
        .select('address')
        .eq('id', user.id)
        .single();

      if (profile?.address) {
        setShippingAddress(profile.address);
      }
    } catch (err) {
      console.error('Error checking user:', err);
      setError('Failed to load user information');
    }
  };

  const handleAddressUpdate = async (addressData: any) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          address: addressData.address,
          phone: addressData.phone,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (error) throw error;

      setShippingAddress(addressData.address);
      setShowAddressForm(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update address');
    }
  };

  const handleCheckout = async () => {
    if (!selectedPayment) {
      setError('Please select a payment method');
      return;
    }

    if (!shippingAddress) {
      setError('Please add a shipping address');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Please sign in to complete your purchase');

      // Create order with shipping address
      const { data: order, error: orderError } = await supabase
        .from('orders')
        .insert({
          user_id: user.id,
          items: state.items,
          total_amount: state.total,
          shipping_address: shippingAddress,
          payment_method: selectedPayment,
          status: 'pending'
        })
        .select()
        .single();

      if (orderError) throw orderError;

      // Clear cart and redirect to success page
      dispatch({ type: 'CLEAR_CART' });
      navigate('/checkout/success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process order');
      setLoading(false);
    }
  };

  if (state.items.length === 0) {
    navigate('/shop');
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Payment Section */}
          <div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-2xl font-semibold mb-6">Complete Your Purchase</h2>

              {/* Shipping Address */}
              <div className="mb-8">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Shipping Address</h3>
                  <button
                    onClick={() => setShowAddressForm(true)}
                    className="text-yellow-600 hover:text-yellow-700 text-sm"
                  >
                    {shippingAddress ? 'Edit' : 'Add'} Address
                  </button>
                </div>
                
                {shippingAddress ? (
                  <div className="bg-gray-50 p-4 rounded-lg flex items-start space-x-3">
                    <MapPin className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-600 whitespace-pre-line">{shippingAddress}</p>
                  </div>
                ) : (
                  <div className="bg-yellow-50 p-4 rounded-lg text-yellow-700">
                    Please add a shipping address to continue
                  </div>
                )}
              </div>

              {/* Payment Methods */}
              {shippingAddress && (
                <PaymentMethods
                  onPaymentSelect={setSelectedPayment}
                  amount={state.total}
                />
              )}

              {error && (
                <div className="mt-4 p-3 text-sm text-red-700 bg-red-100 rounded">
                  {error}
                </div>
              )}

              <button
                onClick={handleCheckout}
                disabled={loading || !selectedPayment || !shippingAddress}
                className="mt-6 w-full bg-yellow-500 text-white px-6 py-3 rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <Loader className="animate-spin h-5 w-5 mr-2" />
                    Processing...
                  </>
                ) : (
                  'Complete Purchase'
                )}
              </button>
            </div>
          </div>

          {/* Order Summary */}
          <div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-2xl font-semibold mb-6">Order Summary</h2>
              <div className="space-y-4">
                {state.items.map((item) => (
                  <div key={item.id} className="flex items-center gap-4 py-4 border-b">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="h-16 w-16 rounded-lg object-cover"
                    />
                    <div className="flex-1">
                      <h3 className="font-medium">{item.name}</h3>
                      <p className="text-gray-500">Quantity: {item.quantity}</p>
                    </div>
                    <p className="font-medium">
                      {currentCurrency.code === 'CAD' ? 
                        `C$${typeof item.price === 'number' ? item.price.toFixed(2) : item.price}` : 
                        `₹${typeof item.price === 'number' ? item.price.toFixed(2) : item.price}`}
                    </p>
                  </div>
                ))}

                <div className="pt-4">
                  <div className="flex justify-between text-sm">
                    <span>Subtotal</span>
                    <span>
                      {currentCurrency.code === 'CAD' ? 
                        `C$${state.total.toFixed(2)}` : 
                        `₹${state.total.toFixed(2)}`}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm mt-2">
                    <span>Shipping</span>
                    <span>Free</span>
                  </div>
                  <div className="flex justify-between text-lg font-semibold mt-4 pt-4 border-t">
                    <span>Total</span>
                    <span>
                      {currentCurrency.code === 'CAD' ? 
                        `C$${state.total.toFixed(2)}` : 
                        `₹${state.total.toFixed(2)}`}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showAddressForm && (
        <AddressForm
          onSubmit={handleAddressUpdate}
          onClose={() => setShowAddressForm(false)}
        />
      )}
    </div>
  );
};

export default Checkout;